/**
 * Carriers and Customers Data Mapper
 * Transforms API responses for carriers and customers tabs
 */

/**
 * Map carriers data to chart format
 */
export const mapCarriersData = (apiData) => {
  const carriers = apiData?.carriers || {};
  const summary = carriers?.summary || {};
  const topCarriers = carriers?.topCarriers || [];

  // Donut chart: Carriers by status
  const totalCarriers = summary.totalCarriers || 0;
  const active = summary.active || 0;
  const suspended = summary.suspended || 0;
  const inactive = summary.inactive || 0;

  const segments = [
    { label: 'Active', value: calculatePercent(active, totalCarriers), slug: 'active' },
    { label: 'Suspended', value: calculatePercent(suspended, totalCarriers), slug: 'suspended' },
    { label: 'Inactive', value: calculatePercent(inactive, totalCarriers), slug: 'inactive' }
  ].filter(seg => seg.value > 0);

  // Bar chart: Top carriers by expense
  const barData = topCarriers.slice(0, 7).map(carrier => ({
    label: carrier.name || carrier.companyName || 'Unknown',
    expense: carrier.expense || 0,
    loads: carrier.loadsCount || 0
  }));

  // Line chart: Carriers performance over time
  const lineCategories = generateDefaultCategories();
  const expenseData = topCarriers.slice(0, 7).map(c => c.expense || 0);
  const loadsData = topCarriers.slice(0, 7).map(c => c.loadsCount || 0);
  
  // Ensure data arrays match categories length
  const paddedExpenseData = expenseData.length > 0 
    ? expenseData 
    : new Array(lineCategories.length).fill(0);
  const paddedLoadsData = loadsData.length > 0 
    ? loadsData 
    : new Array(lineCategories.length).fill(0);
  
  const lineSeries = [
    {
      name: 'Total Expense',
      slug: 'expense',
      data: paddedExpenseData
    },
    {
      name: 'Loads Count',
      slug: 'loads',
      data: paddedLoadsData
    }
  ];

  return {
    donut: {
      title: 'Carriers',
      totalLabel: 'Total',
      total: totalCarriers,
      segments
    },
    bar: {
      title: 'Top Carriers by Expense',
      legend: [
        { label: 'Expense', slug: 'expense', key: 'expense' },
        { label: 'Loads', slug: 'loads', key: 'loads' }
      ],
      data: barData
    },
    line: {
      title: 'Carriers Performance',
      subtitle: 'Top carriers comparison',
      categories: lineCategories,
      series: lineSeries
    }
  };
};

/**
 * Map customers data to chart format
 */
export const mapCustomersData = (apiData) => {
  const customers = apiData?.customers || {};
  const summary = customers?.summary || {};
  const topCustomers = customers?.topCustomers || [];

  // Donut chart: Customers by status
  const totalCustomers = summary.totalCustomers || 0;
  const active = summary.active || 0;
  const suspended = summary.suspended || 0;
  const inactive = summary.inactive || 0;

  const segments = [
    { label: 'Active', value: calculatePercent(active, totalCustomers), slug: 'active' },
    { label: 'Suspended', value: calculatePercent(suspended, totalCustomers), slug: 'suspended' },
    { label: 'Inactive', value: calculatePercent(inactive, totalCustomers), slug: 'inactive' }
  ].filter(seg => seg.value > 0);

  // Bar chart: Top customers by revenue
  const barData = topCustomers.slice(0, 7).map(customer => ({
    label: customer.name || customer.companyName || 'Unknown',
    revenue: customer.revenue || 0,
    profit: customer.profit || 0
  }));

  // Line chart: Customers performance over time
  const lineCategories = generateDefaultCategories();
  const revenueData = topCustomers.slice(0, 7).map(c => c.revenue || 0);
  const profitData = topCustomers.slice(0, 7).map(c => c.profit || 0);
  
  // Ensure data arrays match categories length
  const paddedRevenueData = revenueData.length > 0 
    ? revenueData 
    : new Array(lineCategories.length).fill(0);
  const paddedProfitData = profitData.length > 0 
    ? profitData 
    : new Array(lineCategories.length).fill(0);
  
  const lineSeries = [
    {
      name: 'Total Revenue',
      slug: 'revenue',
      data: paddedRevenueData
    },
    {
      name: 'Total Profit',
      slug: 'profit',
      data: paddedProfitData
    }
  ];

  return {
    donut: {
      title: 'Customers',
      totalLabel: 'Total',
      total: totalCustomers,
      segments
    },
    bar: {
      title: 'Top Customers by Revenue',
      legend: [
        { label: 'Revenue', slug: 'revenue', key: 'revenue' },
        { label: 'Profit', slug: 'profit', key: 'profit' }
      ],
      data: barData
    },
    line: {
      title: 'Customers Performance',
      subtitle: 'Top customers comparison',
      categories: lineCategories,
      series: lineSeries
    }
  };
};

// Helper functions
const calculatePercent = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

const generateDefaultCategories = () => {
  const today = new Date();
  const categories = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    categories.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return categories;
};
